import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { SolidWallMass, SolidWallSide } from "building-types/walls/wallTypes";
import { WallLOD, type SolidWall } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { DIST_EPSILON } from "geom/core";
import { type PolyLine2, polyLine2New } from "geom/polyLine2Types";
import { wallMassBaseCrossSection } from "./solidWall";
import { rect, openTopRect, openBottomRect, buildSolidWallCrossSectionOutlines } from "./solidWallCrossSectionUtils";
import { wallMassCrossSectionLayers } from "./wallLayers";
import { isSolidWallMass } from "./wallTypeGuards";

/**
 * used specifically in plan
 */

export function solidWallCrossSection({
  wall,
  includeDoors = true,
  includeWindows = true,
  includeWallCuts = true,
  includeZeroThicknessEnds = true,
  closedPolyLines = false,
  wallLayer = WallLayerType.SUBSTRATE,
  wallLOD = WallLOD.L2,
  cutPlane,
  roomFloorAssemblies,
}: {
  wall: SolidWall;
  wallLayer?: WallLayerType;
  wallLOD?: WallLOD;
  includeDoors?: boolean;
  includeWindows?: boolean;
  includeZeroThicknessEnds?: boolean;
  includeWallCuts?: boolean;
  closedPolyLines?: boolean;
  cutPlane?: number;
  roomFloorAssemblies?: Table<FloorAssembly>;
}): PolyLine2[] {
  if (!(wallLayer !== WallLayerType.FINISH)) {
    console.assert(false, `
  FINISH wall layers should only be used for debugging purposes.
  They do not accurately reflect the true cross section of a wall.
  wallSideFinishMassCrossSection should be used instead.
  `);
  }

  const crossSectionMasses = wallMassCrossSectionLayers(wall.mass, wallLayer, wallLOD, cutPlane).filter(
    isSolidWallMass,
  );
  if (!crossSectionMasses.length) {
    // this wall is void at this cutPlane
    return [];
  }

  let includeExteriorCasing = true;
  if (wallLayer !== WallLayerType.FINISH) {
    includeExteriorCasing = false;
  }

  const outlines: PolyLine2[] = [];
  const cutPlaneLevelInches = cutPlane !== undefined ? levelInchesNew(cutPlane) : undefined;

  const { leftSide, rightSide } = wall;

  if (crossSectionMasses.length === 1) {
    const crossSectionMass = crossSectionMasses[0];
    outlines.push(
      ...solidWallMassBaseCrossSection({
        crossSectionMass,
        leftSide,
        rightSide,
        includeDoors,
        includeWindows,
        includeWallCuts,
        includeZeroThicknessEnds,
        includeExteriorCasing,
        closedPolyLines,
        cutPlane: cutPlaneLevelInches,
        roomFloorAssemblies,
      }),
    );

    return outlines;
  } else {
    for (const crossSectionMass of crossSectionMasses) {
      outlines.push(
        ...solidWallMassBaseCrossSection({
          crossSectionMass,
          leftSide,
          rightSide,
          includeDoors,
          includeWindows,
          includeWallCuts,
          includeZeroThicknessEnds,
          includeExteriorCasing,
          closedPolyLines,
          cutPlane: cutPlaneLevelInches,
          roomFloorAssemblies,
        }),
      );
    }
  }

  return outlines;
}

function solidWallMassBaseCrossSection({
  crossSectionMass,
  leftSide,
  rightSide,
  includeDoors = true,
  includeWindows = true,
  includeWallCuts = true,
  includeZeroThicknessEnds = true,
  includeExteriorCasing = true,
  closedPolyLines = false,
  cutPlane,
  roomFloorAssemblies,
}: {
  crossSectionMass: SolidWallMass;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  includeDoors?: boolean;
  includeWindows?: boolean;
  includeZeroThicknessEnds?: boolean;
  includeExteriorCasing?: boolean;
  includeWallCuts?: boolean;
  closedPolyLines?: boolean;
  cutPlane?: LevelInches;
  roomFloorAssemblies?: Table<FloorAssembly>;
}): PolyLine2[] {
  const { leftEdge, rightEdge, leftOffset, rightOffset } = crossSectionMass;
  if (leftOffset + rightOffset < DIST_EPSILON) {
    if (!includeZeroThicknessEnds) {
      return [];
    }
    // this wall has 0 thickness at this cutPlane
    const botLeftX = leftEdge[2];
    const botLeftY = leftEdge[3];

    const topLeftX = leftEdge[0];
    const topLeftY = leftEdge[1];

    const botRightX = rightEdge[0];
    const botRightY = rightEdge[1];

    const topRightX = rightEdge[2];
    const topRightY = rightEdge[3];

    return [
      polyLine2New([botLeftX, botLeftY, botRightX, botRightY]),
      polyLine2New([topLeftX, topLeftY, topRightX, topRightY]),
    ];
  }

  const topRect = closedPolyLines ? rect : openTopRect;
  const bottomRect = closedPolyLines ? rect : openBottomRect;

  const outlines: PolyLine2[] = [];

  const { leftGaps, rightGaps } = buildSolidWallCrossSectionOutlines({
    leftSide,
    rightSide,
    crossSectionMass,
    includeExteriorCasing,
    cutPlane,
    outlineBuilder: (i: number, leftPts: number[], rightPts: number[]) => {
      const [botLeftX, botLeftY, topLeftX, topLeftY] = leftPts.slice(i, i + 4);
      const [botRightX, botRightY, topRightX, topRightY] = rightPts.slice(i, i + 4);

      if (i === 0 && !crossSectionMass.start.isFreeEnd) {
        if (crossSectionMass.start.offsetDifferenceOutline) {
          if (closedPolyLines) {
            const arr: number[] = [
              crossSectionMass.start.offsetDifferenceOutline[0],
              crossSectionMass.start.offsetDifferenceOutline[1],
              crossSectionMass.start.offsetDifferenceOutline[2],
              crossSectionMass.start.offsetDifferenceOutline[3],
              botRightX,
              botRightY,
              topRightX,
              topRightY,
              topLeftX,
              topLeftY,
              botLeftX,
              botLeftY,
              crossSectionMass.start.offsetDifferenceOutline[0],
              crossSectionMass.start.offsetDifferenceOutline[1],
            ];
            outlines.push(arr as any as PolyLine2);
            return;
          }

          if (crossSectionMass.start.rightMiterExtend || crossSectionMass.start.rightMiterExtend === undefined) {
            outlines.push(
              polyLine2New([
                crossSectionMass.start.offsetDifferenceOutline[0],
                crossSectionMass.start.offsetDifferenceOutline[1],
                crossSectionMass.start.offsetDifferenceOutline[2],
                crossSectionMass.start.offsetDifferenceOutline[3],
                botRightX,
                botRightY,
                topRightX,
                topRightY,
                topLeftX,
                topLeftY,
                botLeftX,
                botLeftY,
              ]),
            );
          } else {
            outlines.push(
              polyLine2New([
                botRightX,
                botRightY,
                topRightX,
                topRightY,
                topLeftX,
                topLeftY,
                botLeftX,
                botLeftY,
                crossSectionMass.start.offsetDifferenceOutline[0],
                crossSectionMass.start.offsetDifferenceOutline[1],
                crossSectionMass.start.offsetDifferenceOutline[2],
                crossSectionMass.start.offsetDifferenceOutline[3],
              ]),
            );
          }

          return;
        }

        outlines.push(bottomRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY));
        return;
      }

      if (i === leftPts.length - 4 && !crossSectionMass.end.isFreeEnd) {
        if (crossSectionMass.end.offsetDifferenceOutline) {
          if (closedPolyLines) {
            outlines.push(
              polyLine2New([
                botLeftX,
                botLeftY,
                botRightX,
                botRightY,
                topRightX,
                topRightY,
                crossSectionMass.end.offsetDifferenceOutline[0],
                crossSectionMass.end.offsetDifferenceOutline[1],
                crossSectionMass.end.offsetDifferenceOutline[2],
                crossSectionMass.end.offsetDifferenceOutline[3],
                topLeftX,
                topLeftY,
                botLeftX,
                botLeftY,
              ]),
            );
            return;
          }

          if (crossSectionMass.end.rightMiterExtend || crossSectionMass.end.rightMiterExtend === undefined) {
            outlines.push(
              polyLine2New([
                crossSectionMass.end.offsetDifferenceOutline[0],
                crossSectionMass.end.offsetDifferenceOutline[1],
                crossSectionMass.end.offsetDifferenceOutline[2],
                crossSectionMass.end.offsetDifferenceOutline[3],
                topLeftX,
                topLeftY,
                botLeftX,
                botLeftY,
                botRightX,
                botRightY,
                topRightX,
                topRightY,
              ]),
            );
          } else {
            outlines.push(
              polyLine2New([
                topLeftX,
                topLeftY,
                botLeftX,
                botLeftY,
                botRightX,
                botRightY,
                topRightX,
                topRightY,
                crossSectionMass.end.offsetDifferenceOutline[0],
                crossSectionMass.end.offsetDifferenceOutline[1],
                crossSectionMass.end.offsetDifferenceOutline[2],
                crossSectionMass.end.offsetDifferenceOutline[3],
              ]),
            );
          }

          return;
        }

        outlines.push(topRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY));
        return;
      }

      outlines.push(rect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY));
    },
    includeDoors,
    includeWindows,
    includeWallCuts,
    roomFloorAssemblies,
  });

  // if there are no gaps, we can take this fast path
  if (leftGaps.length === 0 && rightGaps.length === 0) {
    return wallMassBaseCrossSection(crossSectionMass, closedPolyLines);
  }

  return outlines;
}
